import * as actions from './actions';

import { catchError, filter, map, switchMap, withLatestFrom, mergeMap, retry } from 'rxjs/operators';

import { AjaxResponse } from 'rxjs/ajax';
import { Epic } from 'redux-observable';
import { GetTransactionsAPIResponse, TransactionDetails } from 'store/transactions/types';
import { RootAction } from 'store/actions';
import { RootDependencies } from 'store/dependencies';
import type { RootState } from 'store/reducer';
import { isActionOf } from 'typesafe-actions';
import { of, concat } from 'rxjs';
import { transformErrorIntoSerializableError } from 'store/utils';
import { PATCH_STATUSES } from 'shared/consts/const';

const CHECK_IN_STATUS_NOT_AVAILABLE_ERROR = 'Check-in status not available yet';

export const getTransactionsEpic: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(actions.getTransactions.request)),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]) => {
      /* Echoed back on success: the offset these rows were asked for is what says whether they replace
       * the list or extend it, rather than the reducer reading the current length. */
      const offset = payload?.offset ?? 0;

      return apiClient(state)
        .getTransactions(payload)
        .pipe(
          map(({ response }: AjaxResponse<GetTransactionsAPIResponse>) => {
            return actions.getTransactions.success({
              transactions: response.data.transactions,
              offset,
              /* No count on the wire means treat this page as the last one, never as "more". */
              total: response.total ?? offset + response.data.transactions.length,
            });
          }),
          catchError((error: Error) =>
            of(actions.getTransactions.failure({ error: transformErrorIntoSerializableError(error) })),
          ),
        );
    }),
  );

/* Its own epic, so its own `switchMap`: one shared epic would share one cancellation, and a tenant
 * switching tabs mid-flight would cancel the read for the tab they just left. */
export const getPastTransactionsEpic: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(actions.getPastTransactions.request)),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]) => {
      /* Echoed back on success: the offset these rows were asked for is what says whether they replace
       * the list or extend it, rather than the reducer reading the current length. */
      const offset = payload?.offset ?? 0;

      return apiClient(state)
        .getPastTransactions(payload)
        .pipe(
          map(({ response }: AjaxResponse<GetTransactionsAPIResponse>) => {
            return actions.getPastTransactions.success({
              transactions: response.data.transactions,
              offset,
              /* No count on the wire means treat this page as the last one, never as "more". */
              total: response.total ?? offset + response.data.transactions.length,
            });
          }),
          catchError((error: Error) =>
            of(actions.getPastTransactions.failure({ error: transformErrorIntoSerializableError(error) })),
          ),
        );
    }),
  );

export const getTransactionEpic: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(actions.getTransaction.request)),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]) => {
      const checkInEnabled = state.resource?.resource?.check_in;
      let lastResponseData: TransactionDetails | null = null;

      return apiClient(state)
        .getTransaction(payload)
        .pipe(
          switchMap(xhrPayload => {
            lastResponseData = xhrPayload.response.data;
            if (checkInEnabled && !xhrPayload.response.data.details?.check_in?.status) {
              throw new Error(CHECK_IN_STATUS_NOT_AVAILABLE_ERROR);
            }
            return of(actions.getTransaction.success(xhrPayload.response.data));
          }),
          retry({
            count: 4,
            delay: 200,
          }),
          catchError((error: Error) => {
            if (error.message === CHECK_IN_STATUS_NOT_AVAILABLE_ERROR && lastResponseData) {
              return of(actions.getTransaction.success(lastResponseData));
            }
            return of(actions.getTransaction.failure({ error: transformErrorIntoSerializableError(error) }));
          }),
        );
    }),
  );

export const getRedirectTransactionEpic: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(actions.getRedirectTransaction.request)),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]) => {
      return apiClient(state)
        .getRedirectTransaction(payload)
        .pipe(
          map(xhrPayload => {
            return actions.getRedirectTransaction.success(xhrPayload.response.data.transaction);
          }),
          catchError((error: Error) =>
            of(actions.getRedirectTransaction.failure({ error: transformErrorIntoSerializableError(error) })),
          ),
        );
    }),
  );

export const patchTransactionEpic: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(actions.patchTransaction.request)),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]) => {
      return apiClient(state)
        .patchTransaction(payload)
        .pipe(
          mergeMap(() => {
            return payload.status === PATCH_STATUSES.CANCELLED
              ? of(actions.patchTransaction.success())
              : concat(of(actions.patchTransaction.success()), of(actions.getTransactions.request({})));
          }),
          catchError((error: Error) =>
            of(actions.patchTransaction.failure({ error: transformErrorIntoSerializableError(error) })),
          ),
        );
    }),
  );

export const checkInEpic: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(actions.checkIn.request)),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]) => {
      return apiClient(state)
        .checkIn(payload)
        .pipe(
          map(xhrPayload => {
            return actions.checkIn.success(xhrPayload.response.data.check_in);
          }),
          catchError((error: Error) =>
            of(actions.checkIn.failure({ error: transformErrorIntoSerializableError(error) })),
          ),
        );
    }),
  );
